import React, { useEffect, useMemo, useState } from "react";

import {
  PaymentElement,
  StripeWrapper,
  useStripe,
  useElements,
  createPaymentError,
  addCardApi,
  createStripeSetupIntent,
  serializeStripeError,
  DEFAULT_ELEMENT_STYLE_OPTIONS,
  DEFAULT_PAYMENT_ELEMENT_OPTIONS
} from "@stadium/services/stripe";

import { jsonErrorFormatter } from "@stadium/helpers/formatter";
import ButtonWithLoader from "../button/button-with-loader";
import Loaders from "../loaders/loaders";
import TextInput from "../text-input/text-input";

const emptyObj = {};
const emptyArr = [];
const emptyFn = () => null;
const cardNameFieldId = "card-name";
const PAYMENT_ERROR_CONFIG = { timeOut: 0, extendedTimeOut: 0 };

const AddCardForm = (props) => {
  const {
    id = "add-card-form",
    submitBtn,
    classes,
    paymentElementOptions = DEFAULT_PAYMENT_ELEMENT_OPTIONS,
    onPaymentError = emptyFn,
    onSuccess = emptyFn,
    apiClient,
    mailTo,
    showError,
    clearErrors,
  } = props;
  const {
    bodyWrapper = "",
    input = "",
    inputLabel = "",
    actionButtonsWrapper = "",
  } = classes || emptyObj;

  const [isLoading, setLoading] = useState(false);
  const [isCardFormLoading, setIsCardFormLoading] = useState(true);
  const [cardName, setCardName] = useState("");
  const [isCardFormComplete, setIsCardFormComplete] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(clearErrors, emptyArr);

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setLoading(true);

    let confirmSetupData = null;

    try {
      confirmSetupData = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      const { error } = confirmSetupData;

      if (error) {
        const { displayErrorMessage } = serializeStripeError(error, mailTo);
        showError(displayErrorMessage, "", PAYMENT_ERROR_CONFIG);
        setLoading(false);
        onPaymentError(createPaymentError(error));
      }
    } catch (er) {
      showError(
        "Something went wrong, Please contact support for further assistance.",
        "",
        PAYMENT_ERROR_CONFIG
      );
      setLoading(false);
      throw er;
    }

    // eslint-disable-next-line consistent-return

    return addCardApi(apiClient, {
      credit_card: {
        gateway_payment_profile_id: confirmSetupData.setupIntent.payment_method,
        name: cardName,
        api_method: "setup_intent_api",
      },
    })
      .then((resp) => {
        const cardObj = Object.values(resp.spreeCreditCards)[0];
        return onSuccess(cardObj);
      })
      .finally(() => setLoading(false))
      .catch((er) => {
        const error = jsonErrorFormatter(er.response, "credit_card");
        const errorMsg = error.credit_card
          ? `${Object.values(error.credit_card)[0]}`
          : "There was a problem saving your card details.";
        showError(errorMsg);
        throw er;
      });
  };

  const renderBody = () => (
    <>
      {isCardFormLoading && <Loaders.Spinner />}
      <div className={isCardFormLoading ? "d-none" : ""}>
        <div className="form-field">
          <TextInput
            id={cardNameFieldId}
            value={cardName}
            onFocus={clearErrors}
            classes="form-group"
            inputClass={`form-control input-control ${input}`}
            placeholder="Name on Card"
            showAnimatedLine={false}
            onChange={({ value }) => {
              setCardName(value);
            }}
            label={{
              for: cardNameFieldId,
              classes: inputLabel,
              content: "Name on Card",
            }}
          />
          <PaymentElement
            options={paymentElementOptions}
            onReady={() =>
              setTimeout(() => {
                setIsCardFormLoading(false);
              }, 1000)
            }
            onChange={({ complete }) => {
              setIsCardFormComplete(complete);
            }}
            onFocus={clearErrors}
          />
        </div>
      </div>
    </>
  );

  const renderFooter = () => {
    const { text: submitButtonText = "Add", classes: submitButtonClass = "py-0" } =
      submitBtn || emptyObj;
    const isSubmitBtnDisabled =
      isLoading || !stripe || !elements || !cardName?.trim() || !isCardFormComplete;

    return (
      <div className={`d-flex justify-content-center align-items-center ${actionButtonsWrapper}`}>
        <div>
          <ButtonWithLoader
            type="submit"
            isLoading={isLoading}
            className={submitButtonClass}
            disabled={isSubmitBtnDisabled}
            onClick={isSubmitBtnDisabled ? null : handleSubmit}
          >
            {submitButtonText}
          </ButtonWithLoader>
        </div>
      </div>
    );
  };

  return (
    <form id={id} onSubmit={handleSubmit}>
      <div className={bodyWrapper}>{renderBody()}</div>
      <div className="px-md-5 mx-xl-3 mt-md-4">{renderFooter()}</div>
    </form>
  );
};

const WrappedCardForm = (props) => {
  const { apiClient, stripeKey, elementStyleOptions = DEFAULT_ELEMENT_STYLE_OPTIONS } = props;
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    if (!clientSecret) {
      createStripeSetupIntent(apiClient).then((resp) => {
        setClientSecret(resp.client_secret);
      });
    }
  }, []);

  const options = useMemo(() => ({ clientSecret, ...elementStyleOptions }), [clientSecret]);

  if (!clientSecret) {
    return (
      <div className="payment-spinner-block">
        <Loaders.Spinner />
      </div>
    );
  }

  return (
    <StripeWrapper stripeKey={stripeKey} options={options}>
      <AddCardForm {...props} />
    </StripeWrapper>
  );
};

export default WrappedCardForm;
